import React, { useEffect, useRef } from 'react';
import axios from 'axios';
import { useI18n } from '../contexts/I18nContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CACHE_PREFIX = 'pm_tr_';

/**
 * DOM auto-translator.
 * Walks the document for visible English text nodes on every route change,
 * batches them to /api/i18n/translate_batch, caches per-string in localStorage,
 * and swaps text content in place. Skips <code>, <script>, <style>, inputs, and
 * elements marked with data-i18n-skip.
 */
const SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'CODE', 'PRE', 'INPUT', 'TEXTAREA', 'SELECT', 'OPTION', 'CANVAS', 'SVG', 'PATH']);
const MIN_LEN = 2;
const MAX_LEN = 300;

const looksTranslatable = (s) => {
  if (!s || s.length < MIN_LEN || s.length > MAX_LEN) return false;
  const stripped = s.trim();
  if (!stripped) return false;
  // Must contain at least one alphabetic char
  if (!/[A-Za-z]/.test(stripped)) return false;
  // Skip pure numbers, urls, emails, brand-only tokens
  if (/^(https?:\/\/|www\.)/i.test(stripped)) return false;
  if (/^[\d\s.,:;/\\-]+$/.test(stripped)) return false;
  return true;
};

const cacheGet = (lang, s) => {
  try {
    return localStorage.getItem(`${CACHE_PREFIX}${lang}::${s}`);
  } catch { return null; }
};
const cacheSet = (lang, s, v) => {
  try { localStorage.setItem(`${CACHE_PREFIX}${lang}::${s}`, v); } catch { /* quota — ignore */ }
};

export default function AutoTranslate() {
  const { language } = useI18n();
  const runTimer = useRef(null);
  const originalMap = useRef(new WeakMap()); // node -> original English text
  const inFlight = useRef(false);

  useEffect(() => {
    if (!language || language === 'en') {
      // Restore originals on reverting to English
      const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT);
      let n;
      while ((n = walker.nextNode())) {
        const orig = originalMap.current.get(n);
        if (orig !== undefined && n.nodeValue !== orig) n.nodeValue = orig;
      }
      return;
    }

    const collectAndTranslate = async () => {
      if (inFlight.current) return;
      inFlight.current = true;
      try {
        const strings = new Set();
        const nodesByString = new Map(); // src -> [textNode,...]
        const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, {
          acceptNode(node) {
            const p = node.parentElement;
            if (!p) return NodeFilter.FILTER_REJECT;
            if (SKIP_TAGS.has(p.tagName)) return NodeFilter.FILTER_REJECT;
            if (p.closest('[data-i18n-skip]')) return NodeFilter.FILTER_REJECT;
            if (p.closest('[contenteditable="true"]')) return NodeFilter.FILTER_REJECT;
            const rawTxt = node.nodeValue;
            if (!looksTranslatable(rawTxt)) return NodeFilter.FILTER_REJECT;
            return NodeFilter.FILTER_ACCEPT;
          },
        });
        let node;
        while ((node = walker.nextNode())) {
          // Remember original English exactly once
          if (!originalMap.current.has(node)) originalMap.current.set(node, node.nodeValue);
          const src = originalMap.current.get(node);
          const key = src.trim();
          if (!key) continue;
          const cached = cacheGet(language, key);
          if (cached) {
            // Preserve original whitespace
            const leading = src.match(/^\s*/)[0];
            const trailing = src.match(/\s*$/)[0];
            node.nodeValue = leading + cached + trailing;
            continue;
          }
          strings.add(key);
          if (!nodesByString.has(key)) nodesByString.set(key, []);
          nodesByString.get(key).push({ node, src });
        }

        // Also translate common attributes on live elements so placeholders / tooltips / aria labels
        // aren't left in English while the visible text is translated.
        const attrsToTranslate = ['placeholder', 'title', 'aria-label', 'alt'];
        const attrTargets = []; // {el, attr, key}
        document.querySelectorAll('input, textarea, button, a, img, [title], [aria-label], [placeholder]').forEach(el => {
          if (el.closest('[data-i18n-skip]')) return;
          attrsToTranslate.forEach(attr => {
            const val = el.getAttribute(attr);
            if (!val || !looksTranslatable(val)) return;
            const k = val.trim();
            const cached = cacheGet(language, k);
            if (cached) { el.setAttribute(attr, cached); return; }
            strings.add(k);
            attrTargets.push({ el, attr, key: k });
          });
        });

        if (strings.size === 0) return;
        const chunkSize = 60;
        const arr = Array.from(strings);
        for (let i = 0; i < arr.length; i += chunkSize) {
          const chunk = arr.slice(i, i + chunkSize);
          try {
            const r = await axios.post(`${API}/i18n/translate_batch`, { lang: language, strings: chunk });
            const t = r.data?.translations || {};
            for (const src of chunk) {
              const translated = t[src];
              if (!translated || translated === src) continue;
              cacheSet(language, src, translated);
              const targets = nodesByString.get(src) || [];
              for (const { node: n, src: original } of targets) {
                const leading = original.match(/^\s*/)[0];
                const trailing = original.match(/\s*$/)[0];
                if (n && document.contains(n)) n.nodeValue = leading + translated + trailing;
              }
              // Apply to any attribute targets waiting for this string
              attrTargets.forEach(({ el, attr, key }) => {
                if (key === src && document.contains(el)) {
                  try { el.setAttribute(attr, translated); } catch (_) { /* readonly attr */ }
                }
              });
            }
          } catch { /* ignore chunk failure */ }
        }
      } finally {
        inFlight.current = false;
      }
    };

    // Debounced run + MutationObserver so route changes/new content get translated
    const schedule = () => {
      if (runTimer.current) clearTimeout(runTimer.current);
      runTimer.current = setTimeout(collectAndTranslate, 250);
    };
    schedule();
    const mo = new MutationObserver(schedule);
    mo.observe(document.body, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['placeholder', 'title', 'aria-label', 'alt'] });
    return () => { mo.disconnect(); if (runTimer.current) clearTimeout(runTimer.current); };
  }, [language]);

  return null;
}
